import React, { useState } from 'react';
import { useAppContext } from '../App';

const FactoriesCarousel = ({ factories }) => {
  const { showNotification } = useAppContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;
  const maxIndex = Math.max(0, factories.length - itemsPerPage);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const handleFactoryClick = (factory) => {
    showNotification(`Viewing ${factory.name} - ${factory.specialties.join(', ')}`, 'info');
  };

  const visibleFactories = factories.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <div className="carousel-container">
      <div className="carousel-track">
        {visibleFactories.map((factory, index) => (
          <div
            key={factory.id}
            className="factory-card"
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={() => handleFactoryClick(factory)}
          >
            <div className="factory-image">
              <img src={factory.image} alt={factory.name} />
              {factory.verified && (
                <div className="verified-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="factory-info">
              <h3 className="factory-name">{factory.name}</h3>
              <div className="factory-location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {factory.location}
              </div>
              <div className="factory-meta">
                <div className="rating">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span>{factory.rating}</span>
                </div>
                <div className="response-time">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{factory.responseTime}</span>
                </div>
              </div>
              <div className="specialties">
                {factory.specialties.slice(0, 2).map((specialty, i) => (
                  <span key={i} className="specialty-tag">
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="carousel-controls">
        <button
          className="carousel-btn"
          onClick={handlePrev}
          disabled={currentIndex === 0}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="carousel-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              className={`dot ${currentIndex === i ? 'active' : ''}`}
              onClick={() => setCurrentIndex(i)}
            />
          ))}
        </div>
        <button
          className="carousel-btn"
          onClick={handleNext}
          disabled={currentIndex >= maxIndex}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <style jsx>{`
        .carousel-container {
          position: relative;
        }

        .carousel-track {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-md);
          margin-bottom: var(--space-lg);
        }

        .factory-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
          overflow: hidden;
          transition: all var(--transition-base);
          animation: fadeInUp 0.5s ease forwards;
          opacity: 0;
          cursor: pointer;
        }

        .factory-card:hover {
          transform: translateY(-8px);
          border-color: var(--color-accent-400);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
        }

        .factory-image {
          position: relative;
          height: 140px;
          overflow: hidden;
        }

        .factory-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: grayscale(30%);
          transition: all var(--transition-base);
        }

        .factory-card:hover .factory-image img {
          filter: grayscale(0%);
          transform: scale(1.05);
        }

        .verified-badge {
          position: absolute;
          top: var(--space-sm);
          right: var(--space-sm);
          width: 28px;
          height: 28px;
          background: rgba(16, 185, 129, 0.95);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .factory-info {
          padding: var(--space-md);
        }

        .factory-name {
          font-family: var(--font-display);
          font-size: var(--font-base);
          font-weight: 600;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-xs);
          line-height: 1.3;
        }

        .factory-location {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-size: var(--font-sm);
          color: var(--color-neutral-400);
          margin-bottom: var(--space-sm);
        }

        .factory-meta {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          margin-bottom: var(--space-sm);
        }

        .rating,
        .response-time {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-size: var(--font-sm);
          color: var(--color-neutral-300);
        }

        .rating svg {
          color: var(--color-accent-400);
        }

        .specialties {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }

        .specialty-tag {
          font-size: var(--font-xs);
          padding: 2px var(--space-sm);
          background: rgba(59, 130, 246, 0.15);
          color: var(--color-accent-300);
          border-radius: var(--radius-sm);
        }

        .carousel-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-md);
        }

        .carousel-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 50%;
          color: var(--color-neutral-300);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .carousel-btn:hover:not(:disabled) {
          background: rgba(59, 130, 246, 0.2);
          border-color: var(--color-accent-400);
          color: var(--color-accent-400);
        }

        .carousel-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .carousel-dots {
          display: flex;
          gap: var(--space-sm);
        }

        .dot {
          width: 8px;
          height: 8px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 50%;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .dot.active {
          background: var(--color-accent-400);
          width: 24px;
          border-radius: 4px;
        }

        @media (max-width: 1024px) {
          .carousel-track {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 640px) {
          .carousel-track {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default FactoriesCarousel;
